import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getThemeDiscussionPalette } from '../../config/themes';
import { MarkdownPreview } from './NodeContent';

const MAX_RENDERED_MARKDOWN_CHARACTERS = 250_000;
const MAX_MARKDOWN_BYTES = 2 * 1024 * 1024;
const MAX_MARKDOWN_DESCRIPTION_CHARACTERS = 500;
const ATTACHMENT_HISTORY_STATE_KEY = 'mindmappiAttachmentViewer';
const MARKDOWN_INDENT = '  ';

function stopCanvasGesture(event) {
  event.stopPropagation();
}

export function applyMarkdownTabEdit(value, selectionStart, selectionEnd, outdent = false) {
  const source = String(value || '');
  const start = Math.max(0, Math.min(selectionStart, source.length));
  const end = Math.max(start, Math.min(selectionEnd, source.length));
  if (!outdent && start === end) {
    return {
      value: `${source.slice(0, start)}${MARKDOWN_INDENT}${source.slice(end)}`,
      selectionStart: start + MARKDOWN_INDENT.length,
      selectionEnd: start + MARKDOWN_INDENT.length,
    };
  }

  const firstLineStart = source.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
  const edits = [];
  let lineStart = firstLineStart;
  while (lineStart <= source.length && (lineStart < end || start === end)) {
    if (outdent) {
      const line = source.slice(lineStart);
      const removable = line.startsWith('\t') ? 1 : (line.match(/^ {1,2}/)?.[0].length || 0);
      if (removable) edits.push({ offset: lineStart, remove: removable, insert: '' });
    } else {
      edits.push({ offset: lineStart, remove: 0, insert: MARKDOWN_INDENT });
    }
    const nextBreak = source.indexOf('\n', lineStart);
    if (nextBreak === -1 || nextBreak + 1 >= end) break;
    lineStart = nextBreak + 1;
  }

  if (!edits.length) return { value: source, selectionStart: start, selectionEnd: end };
  let nextValue = source;
  [...edits].reverse().forEach(({ offset, remove, insert }) => {
    nextValue = `${nextValue.slice(0, offset)}${insert}${nextValue.slice(offset + remove)}`;
  });
  const adjustPosition = (position, keepAtFirstLineStart = false) => edits.reduce(
    (adjusted, { offset, remove, insert }, index) => {
      if (keepAtFirstLineStart && index === 0 && position === offset) return adjusted;
      if (offset >= position) return adjusted;
      return adjusted + insert.length - Math.min(remove, position - offset);
    },
    position
  );

  return {
    value: nextValue,
    selectionStart: adjustPosition(start, start !== end),
    selectionEnd: adjustPosition(end),
  };
}

function normalizedMarkdownFilename(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  return /\.md$/i.test(trimmed) ? `${trimmed.slice(0, -3)}.md` : `${trimmed}.md`;
}

function hasForbiddenFilenameCharacter(value) {
  return [...String(value || '')].some((character) => {
    const code = character.charCodeAt(0);
    return character === '/' || character === '\\' || code <= 31 || code === 127;
  });
}

function normalizedMarkdownDescription(value) {
  return String(value || '').trim();
}

function AttachmentViewerModal({
  attachment,
  content,
  objectUrl,
  isLoading,
  error,
  isDarkMode,
  theme,
  onClose,
  onDownload,
  canEdit = false,
  onSave = null,
}) {
  const [markdownView, setMarkdownView] = useState('preview');
  const [savedContent, setSavedContent] = useState('');
  const [draftContent, setDraftContent] = useState('');
  const [savedFilename, setSavedFilename] = useState('');
  const [draftFilename, setDraftFilename] = useState('');
  const [savedDescription, setSavedDescription] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const closeGuardRef = useRef(() => true);
  const onCloseRef = useRef(onClose);
  const historyEntryRef = useRef(null);
  const suppressNextPopRef = useRef(false);
  const palette = getThemeDiscussionPalette(theme, isDarkMode);
  const isPdf = attachment?.kind === 'pdf';
  const isMarkdown = attachment?.kind === 'markdown';
  const markdownContent = typeof content === 'string' ? content : '';
  const displayedContent = isMarkdown && canEdit ? draftContent : markdownContent;
  const previewWasTruncated = isMarkdown && displayedContent.length > MAX_RENDERED_MARKDOWN_CHARACTERS;
  const previewContent = previewWasTruncated
    ? displayedContent.slice(0, MAX_RENDERED_MARKDOWN_CHARACTERS)
    : displayedContent;
  const draftBytes = useMemo(() => (
    typeof TextEncoder === 'undefined'
      ? draftContent.length
      : new TextEncoder().encode(draftContent).length
  ), [draftContent]);
  const normalizedDraftFilename = normalizedMarkdownFilename(draftFilename);
  const normalizedDraftDescription = normalizedMarkdownDescription(draftDescription);
  const filenameError = isMarkdown && canEdit && (
    !normalizedDraftFilename
      ? 'Enter a filename.'
      : normalizedDraftFilename.length > 255
        ? 'Filenames can be up to 255 characters.'
        : hasForbiddenFilenameCharacter(normalizedDraftFilename)
          ? 'Filenames cannot contain slashes or control characters.'
          : ''
  );
  const isDirty = isMarkdown && canEdit && (
    draftContent !== savedContent
    || normalizedDraftFilename !== normalizedMarkdownFilename(savedFilename)
    || normalizedDraftDescription !== normalizedMarkdownDescription(savedDescription)
  );
  const saveDisabled = (
    !isDirty
    || isSaving
    || draftBytes > MAX_MARKDOWN_BYTES
    || Boolean(filenameError)
    || typeof onSave !== 'function'
  );

  useEffect(() => {
    setMarkdownView('preview');
    setSavedContent(markdownContent);
    setDraftContent(markdownContent);
    setSavedFilename(attachment?.filename || 'untitled.md');
    setDraftFilename(attachment?.filename || 'untitled.md');
    setSavedDescription(attachment?.description || '');
    setDraftDescription(attachment?.description || '');
    setSaveError('');
    setIsSaving(false);
  }, [attachment?.assetId, attachment?.description, attachment?.filename, markdownContent]);

  onCloseRef.current = onClose;

  const canClose = useCallback(() => {
    if (isSaving) return false;
    if (
      isDirty
      && typeof window !== 'undefined'
      && !window.confirm('Discard your unsaved Markdown changes?')
    ) {
      return false;
    }
    return true;
  }, [isDirty, isSaving]);
  closeGuardRef.current = canClose;

  const requestClose = useCallback(() => {
    if (!canClose()) return;
    const historyEntry = historyEntryRef.current;
    if (
      historyEntry
      && typeof window !== 'undefined'
      && window.history?.state?.[ATTACHMENT_HISTORY_STATE_KEY] === historyEntry
    ) {
      suppressNextPopRef.current = true;
      historyEntryRef.current = null;
      window.history.back();
    }
    onClose?.();
  }, [canClose, onClose]);

  const saveMarkdown = useCallback(async () => {
    if (saveDisabled) return;
    setIsSaving(true);
    setSaveError('');
    try {
      const savedAttachment = await onSave(
        draftContent,
        normalizedDraftFilename,
        normalizedDraftDescription
      );
      setSavedContent(draftContent);
      const nextFilename = savedAttachment?.filename || normalizedDraftFilename;
      const nextDescription = savedAttachment?.description ?? normalizedDraftDescription;
      setSavedFilename(nextFilename);
      setDraftFilename(nextFilename);
      setSavedDescription(nextDescription);
      setDraftDescription(nextDescription);
      setMarkdownView('preview');
    } catch (saveFailure) {
      setSaveError(saveFailure?.message || 'Could not save this Markdown file.');
    } finally {
      setIsSaving(false);
    }
  }, [
    draftContent,
    normalizedDraftDescription,
    normalizedDraftFilename,
    onSave,
    saveDisabled,
  ]);

  const cancelMarkdownEdit = useCallback(() => {
    setDraftContent(savedContent);
    setDraftFilename(savedFilename);
    setDraftDescription(savedDescription);
    setSaveError('');
    setMarkdownView('preview');
  }, [savedContent, savedDescription, savedFilename]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        requestClose();
        return;
      }
      if (
        isMarkdown
        && canEdit
        && (event.ctrlKey || event.metaKey)
        && String(event.key).toLowerCase() === 's'
      ) {
        event.preventDefault();
        saveMarkdown();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canEdit, isMarkdown, requestClose, saveMarkdown]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.history?.pushState) return undefined;
    const historyEntry = `attachment-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const previousState = window.history.state;
    const currentUrl = window.location.href;
    const overlayState = {
      ...(previousState && typeof previousState === 'object' ? previousState : {}),
      [ATTACHMENT_HISTORY_STATE_KEY]: historyEntry,
    };

    window.history.pushState(overlayState, '', currentUrl);
    historyEntryRef.current = historyEntry;

    const handlePopState = () => {
      if (suppressNextPopRef.current) {
        suppressNextPopRef.current = false;
        return;
      }
      if (historyEntryRef.current !== historyEntry) return;
      if (!closeGuardRef.current()) {
        window.history.pushState(overlayState, '', currentUrl);
        return;
      }
      historyEntryRef.current = null;
      onCloseRef.current?.();
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (historyEntryRef.current === historyEntry) {
        if (window.history.state?.[ATTACHMENT_HISTORY_STATE_KEY] === historyEntry) {
          window.history.replaceState(previousState, '', currentUrl);
        }
        historyEntryRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!isDirty || typeof window === 'undefined') return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  return (
    <div
      className="attachment-viewer-backdrop"
      role="presentation"
      data-export-ignore="true"
      onMouseDown={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget) requestClose();
      }}
      onMouseMove={stopCanvasGesture}
      onMouseUp={stopCanvasGesture}
      onPointerDown={stopCanvasGesture}
      onPointerMove={stopCanvasGesture}
      onPointerUp={stopCanvasGesture}
      onPointerCancel={stopCanvasGesture}
      onClick={stopCanvasGesture}
      onDoubleClick={stopCanvasGesture}
      onContextMenu={stopCanvasGesture}
      onWheel={stopCanvasGesture}
    >
      <section
        className="attachment-viewer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="attachment-viewer-title"
        style={{
          background: palette.panel,
          color: palette.text,
          borderColor: palette.border,
        }}
      >
        <header className="attachment-viewer__header" style={{ borderColor: palette.border }}>
          <div id="attachment-viewer-title" className="attachment-viewer__title">
            {attachment?.filename || (isMarkdown ? 'Markdown file' : isPdf ? 'PDF file' : 'Image')}
          </div>
          {isMarkdown && !isLoading && !error && (
            <div
              className="attachment-viewer__mode-switch"
              role="tablist"
              aria-label="Markdown view"
              style={{ background: palette.raised }}
            >
              {['preview', canEdit ? 'edit' : 'markdown', 'details'].map((candidate) => (
                <button
                  key={candidate}
                  type="button"
                  role="tab"
                  aria-selected={markdownView === candidate}
                  onClick={() => setMarkdownView(candidate)}
                  style={{
                    background: markdownView === candidate ? palette.accent : 'transparent',
                    color: markdownView === candidate ? palette.accentText : palette.text,
                  }}
                >
                  {candidate === 'preview'
                    ? 'Preview'
                    : candidate === 'edit'
                      ? 'Edit'
                      : candidate === 'markdown'
                        ? 'Markdown'
                        : 'Details'}
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            className="attachment-viewer__close"
            aria-label="Close attachment"
            onClick={requestClose}
            disabled={isSaving}
            style={{ color: palette.text }}
          >
            ×
          </button>
        </header>

        <div className="attachment-viewer__body">
          {isLoading ? (
            <div role="status" className="attachment-viewer__message">Loading attachment…</div>
          ) : error ? (
            <div role="alert" className="attachment-viewer__message">{error}</div>
          ) : isMarkdown ? (
            markdownView === 'details' ? (
              <div className="attachment-viewer__details">
                <label>
                  <span>Filename</span>
                  {canEdit ? (
                    <input
                      aria-label="Markdown filename"
                      value={draftFilename}
                      maxLength={255}
                      onChange={(event) => {
                        setDraftFilename(event.target.value);
                        setSaveError('');
                      }}
                      style={{
                        background: palette.input,
                        color: palette.text,
                        borderColor: palette.border,
                      }}
                    />
                  ) : (
                    <strong>{attachment?.filename || 'untitled.md'}</strong>
                  )}
                </label>
                <label>
                  <span>Description</span>
                  {canEdit ? (
                    <textarea
                      aria-label="Markdown description"
                      value={draftDescription}
                      maxLength={MAX_MARKDOWN_DESCRIPTION_CHARACTERS}
                      rows={5}
                      placeholder="What is this document about?"
                      onChange={(event) => {
                        setDraftDescription(event.target.value);
                        setSaveError('');
                      }}
                      style={{
                        background: palette.input,
                        color: palette.text,
                        borderColor: palette.border,
                      }}
                    />
                  ) : (
                    <p>{attachment?.description || 'No description has been added.'}</p>
                  )}
                </label>
                <p className="attachment-viewer__details-hint">
                  The description is shown on the idea. Leave it blank to show the filename instead.
                </p>
                {canEdit && (
                  <span className="attachment-viewer__details-count">
                    {draftDescription.length} / {MAX_MARKDOWN_DESCRIPTION_CHARACTERS}
                  </span>
                )}
              </div>
            ) : markdownView === 'preview' ? (
              <article className="attachment-viewer__markdown-preview">
                {previewWasTruncated && (
                  <p className="attachment-viewer__notice" role="status">
                    This file is large, so the rendered preview is shortened. Switch to {canEdit ? 'Edit' : 'Markdown'} to view all of it.
                  </p>
                )}
                <MarkdownPreview source={previewContent || ''} />
              </article>
            ) : canEdit ? (
              <div className="attachment-viewer__markdown-editor-wrap">
                <textarea
                  className="attachment-viewer__markdown-editor"
                  aria-label="Markdown source"
                  autoFocus
                  value={draftContent}
                  onChange={(event) => {
                    setDraftContent(event.target.value);
                    setSaveError('');
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Tab') return;
                    event.preventDefault();
                    const editor = event.currentTarget;
                    const edit = applyMarkdownTabEdit(
                      draftContent,
                      editor.selectionStart,
                      editor.selectionEnd,
                      event.shiftKey
                    );
                    setDraftContent(edit.value);
                    setSaveError('');
                    window.setTimeout(() => {
                      editor.setSelectionRange(edit.selectionStart, edit.selectionEnd);
                    }, 0);
                  }}
                  spellCheck="true"
                  style={{
                    background: palette.input,
                    color: palette.text,
                    borderColor: palette.border,
                  }}
                />
                <div className="attachment-viewer__editor-status">
                  <span>{draftBytes.toLocaleString()} / {MAX_MARKDOWN_BYTES.toLocaleString()} bytes</span>
                  {draftBytes > MAX_MARKDOWN_BYTES && (
                    <span role="alert">Markdown files can be up to 2 MB.</span>
                  )}
                  {saveError && <span role="alert">{saveError}</span>}
                </div>
              </div>
            ) : (
              <pre className="attachment-viewer__markdown-source" style={{ background: palette.input }}>
                <code>{markdownContent}</code>
              </pre>
            )
          ) : isPdf && objectUrl ? (
            <object
              className="attachment-viewer__pdf"
              data={objectUrl}
              type="application/pdf"
              aria-label={`PDF preview: ${attachment?.filename || 'Attached PDF'}`}
            >
              <p>This browser cannot display the PDF. Download it to review the document.</p>
            </object>
          ) : objectUrl ? (
            <img
              className="attachment-viewer__image"
              src={objectUrl}
              alt={attachment?.filename || 'Attached image'}
            />
          ) : null}
        </div>

        <footer className="attachment-viewer__footer" style={{ borderColor: palette.border }}>
          <span>
            {isMarkdown
              ? canEdit
                ? isSaving
                  ? 'Saving Markdown…'
                  : isDirty
                    ? 'Unsaved Markdown changes'
                    : 'Markdown is saved'
                : 'Markdown preview is read only.'
              : isPdf ? 'PDF preview is read only.' : 'Full image preview'}
          </span>
          {filenameError && <span role="alert">{filenameError}</span>}
          {isMarkdown && canEdit && isDirty && (
            <>
              <button
                type="button"
                onClick={cancelMarkdownEdit}
                disabled={isSaving}
                style={{ background: palette.raised, color: palette.text }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveMarkdown}
                disabled={saveDisabled}
                style={{ background: palette.accent, color: palette.accentText }}
              >
                {isSaving ? 'Saving…' : 'Save'}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onDownload}
            disabled={isLoading || Boolean(error)}
            style={{ background: palette.accent, color: palette.accentText }}
          >
            Download
          </button>
        </footer>
      </section>
    </div>
  );
}

export default AttachmentViewerModal;
